import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { filter, map, take } from 'rxjs';

import { EstablishmentStore } from '../../establishment/store/establishment.store';
import { OnboardingStore } from '../store/onboarding.store';

/**
 * Resolves the reverse guard decision.
 *
 * - Active establishments are redirected to /dashboard (no onboarding needed).
 * - Complete owner users are redirected to /dashboard (they don't need onboarding).
 * - Non-owners and incomplete users pass through to the onboarding routes.
 * - undefined isComplete (still loading) passes through — don't redirect while loading.
 */
function resolveReverseGuard(
  isComplete: boolean | undefined,
  isOwner: boolean,
  isEstablishmentActive: boolean,
  router: Router,
): boolean | UrlTree {
  // Active establishments don't need onboarding — redirect to dashboard
  if (isEstablishmentActive) {
    return router.createUrlTree(['/dashboard']);
  }

  // Only redirect complete owners — they shouldn't access onboarding routes
  if (isComplete === true && isOwner) {
    return router.createUrlTree(['/dashboard']);
  }

  // All others pass through: incomplete users, non-owners, loading state
  return true;
}

/**
 * Reverse guard that prevents complete users from accessing onboarding routes.
 *
 * Applied to /onboarding/complete and /onboarding/contract routes.
 * Prevents complete users from manually navigating to stale onboarding screens.
 *
 * Reads from:
 * - OnboardingStore.isComplete() — three-state signal (true/false/undefined)
 * - OnboardingStore.isLoading — combined loading state
 * - EstablishmentStore.isOwner() — role check
 *
 * Behavior:
 * - Fast-path: If not loading, evaluates immediately
 * - Async path: If loading, returns Observable that waits for loading to resolve
 * - Complete owner users are redirected to /dashboard
 * - All other users pass through to onboarding routes
 */
export const reverseOnboardingGuard: CanActivateFn = () => {
  const onboardingStore = inject(OnboardingStore);
  const establishmentStore = inject(EstablishmentStore);
  const router = inject(Router);

  // Fast-path: if not loading, check immediately
  if (!onboardingStore.isLoading()) {
    return resolveReverseGuard(
      onboardingStore.isComplete(),
      establishmentStore.isOwner(),
      establishmentStore.isEstablishmentActive(),
      router,
    );
  }

  // Async path: wait for loading to complete, then evaluate
  return toObservable(onboardingStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() =>
      resolveReverseGuard(
        onboardingStore.isComplete(),
        establishmentStore.isOwner(),
        establishmentStore.isEstablishmentActive(),
        router,
      ),
    ),
  );
};
